import { decimalToUnits } from './amount/money.js';
/** Raw provider details remain available deliberately, but never in serialized diagnostics or UI copy. */
export class FundingProviderError extends Error {
    failure;
    #providerError;
    constructor(error, amount) {
        const code = safeCode(error.code);
        const category = requiresPaymentMethodConnection(error)
            ? 'connection_required'
            : categorize(code);
        super(category);
        this.name = 'FundingProviderError';
        const amountBoundary = category === 'invalid_request' && amount && amount.domain !== 'receive'
            ? sourceAmountBoundary(error, amount)
            : null;
        const sourceBounds = category === 'invalid_request' && amount?.fiatCurrency
            ? readSourceBounds(error, amount.fiatCurrency, amount.fiatDecimals)
            : null;
        const destinationBounds = category === 'invalid_request' && amount?.destination
            ? readSourceBounds(error, amount.destination.currency, amount.destination.decimals, 'destination.amount')
            : null;
        this.failure = {
            product: 'platform',
            code,
            category,
            ...(amountBoundary ? { amountBoundary } : {}),
            ...(sourceBounds ? { sourceBounds } : {}),
            ...(destinationBounds ? { destinationBounds } : {}),
        };
        this.#providerError = error;
    }
    get providerError() {
        return this.#providerError;
    }
}
export function providerFailure(error) {
    return error instanceof FundingProviderError ? error.failure : null;
}
function requiresPaymentMethodConnection(error) {
    if (error.code !== 'invalid_request' || error.errors?.length !== 1)
        return false;
    const detail = error.errors[0];
    return Boolean(detail?.field === 'paymentMethod' &&
        /^"(?:paypal|venmo|revolut_pay|moonpay_balance|sepa|fps|fps_open_banking|pix)" requires active connection$/.test(detail.message));
}
/** Bounds retain the provider source denomination; they are not receive-target bounds. */
function readSourceBounds(error, currency, decimals, field = 'source.amount') {
    const detail = error.errors?.find((entry) => entry.field === field);
    if (!detail || !('details' in detail) || !detail.details || typeof detail.details !== 'object')
        return null;
    const values = detail.details;
    const bounds = { currency, decimals };
    try {
        if (typeof values.minBuyAmount === 'string')
            bounds.minimumUnits = decimalToUnits(values.minBuyAmount, decimals, 99999999999999999999n).toString();
        if (typeof values.maxBuyAmount === 'string')
            bounds.maximumUnits = decimalToUnits(values.maxBuyAmount, decimals, 99999999999999999999n).toString();
    }
    catch {
        return null;
    }
    return bounds.minimumUnits !== undefined || bounds.maximumUnits !== undefined ? bounds : null;
}
function sourceAmountBoundary(error, amount) {
    if (!/^[1-9]\d{0,19}$/.test(amount.principalUnits))
        return null;
    const detail = error.errors?.find((entry) => entry.field === 'source.amount');
    if (!detail || !('details' in detail) || !detail.details || typeof detail.details !== 'object')
        return null;
    const values = detail.details;
    const candidates = [
        { kind: 'minimum', value: values.minBuyAmount },
        { kind: 'maximum', value: values.maxBuyAmount },
    ];
    for (const { kind, value } of candidates) {
        if (typeof value !== 'string')
            continue;
        try {
            const units = decimalToUnits(value, amount.fiatDecimals, 99999999999999999999n);
            const principal = BigInt(amount.principalUnits);
            if (units > 0n && (kind === 'minimum' ? principal < units : principal > units))
                return { kind, amountUnits: units.toString() };
        }
        catch {
            // Malformed provider boundaries are not safe to use as financial input.
        }
    }
    return null;
}
function categorize(code) {
    switch (code) {
        case 'unauthorized':
        case 'connection_required':
            return 'connection_required';
        case 'connection_pending':
            return 'verification_pending';
        case 'requirements_incomplete':
            return 'verification_required';
        case 'verification_rejected':
            return 'verification_rejected';
        case 'country_mismatch':
        case 'unsupported_country':
            return 'region_unavailable';
        case 'too_many_requests':
            return 'rate_limited';
        case 'invalid_request':
        case 'unprocessable_entity':
            return 'invalid_request';
        case 'forbidden':
        case 'not_allowed':
            return 'access_denied';
        case 'conflict':
            return 'request_conflict';
        default:
            return 'provider_unavailable';
    }
}
function safeCode(code) {
    const documented = [
        'unknown_error',
        'unauthorized',
        'forbidden',
        'not_found',
        'invalid_request',
        'not_implemented',
        'too_many_requests',
        'not_allowed',
        'conflict',
        'unprocessable_entity',
        'sse_timeout',
        'sse_error',
        'connection_required',
        'connection_pending',
        'connection_unavailable',
        'service_unavailable',
        'requirements_incomplete',
        'verification_rejected',
        'country_mismatch',
        'unsupported_country',
    ];
    return documented.includes(code) ? code : 'unknown_error';
}
